import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import { getAvailableDates, getAvailableTimeSlots, bookDemoCall } from 'services/bookingService';

const BookingSection = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  
  // Current month/year state for calendar
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  // Available dates and times
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // User's timezone
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  
  // Fetch available dates when month/year changes
  useEffect(() => {
    const fetchDates = async () => {
      setIsLoading(true);
      try {
        const response = await getAvailableDates();
        if (response.success) {
          // Convert string dates to Date objects
          const dates = response.data.map(dateStr => new Date(dateStr));
          setAvailableDates(dates);
        } else {
          console.error('Failed to fetch available dates:', response.message);
          // Fallback to mock data for demo purposes
          fallbackToMockDates();
        }
      } catch (error) {
        console.error('Error fetching available dates:', error);
        // Fallback to mock data for demo purposes
        fallbackToMockDates();
      } finally {
        setIsLoading(false);
      }
    };

    const fallbackToMockDates = () => {
      // Mock data - in production this would come from API
      const currentDate = new Date();
      const dates = [];
      
      // Generate some available dates for the next 30 days
      for (let i = 1; i <= 30; i++) {
        const date = new Date(currentYear, currentMonth, i);
        if (date.getMonth() === currentMonth && date >= currentDate) {
          // Make some days available (mock data)
          if ([2, 3, 4, 5, 6, 9, 10, 16, 17, 18, 19, 20, 23, 24, 25, 26, 27, 30].includes(i)) {
            dates.push(date);
          }
        }
      }
      
      setAvailableDates(dates);
    };
    
    fetchDates();
  }, [currentMonth, currentYear]);
  
  // Fetch available time slots when a date is selected
  useEffect(() => {
    if (!selectedDate) return;
    
    const fetchTimeSlots = async () => {
      setIsLoading(true);
      try {
        // Format date as YYYY-MM-DD
        const formattedDate = selectedDate.toISOString().split('T')[0];
        const response = await getAvailableTimeSlots(formattedDate, timezone);
        
        if (response.success) {
          // Format time slots
          const times = response.data.map(slot => slot.formatted);
          setAvailableTimes(times);
        } else {
          console.error('Failed to fetch available time slots:', response.message);
          // Fallback to mock data for demo purposes
          fallbackToMockTimes();
        }
      } catch (error) {
        console.error('Error fetching available time slots:', error);
        // Fallback to mock data for demo purposes
        fallbackToMockTimes();
      } finally {
        setIsLoading(false);
      }
    };

    const fallbackToMockTimes = () => {
      // Mock data - in production this would come from API
      const times = [
        '12:00am', '12:30am', '1:00am', '1:30am', '2:00am', 
        '7:00pm', '7:30pm'
      ];
      setAvailableTimes(times);
    };
    
    fetchTimeSlots();
  }, [selectedDate, timezone]);
  
  const handleDateClick = (date) => {
    setSelectedDate(date);
    setCurrentStep(2);
  };
  
  const handleTimeClick = (time) => {
    setSelectedTime(time);
    setCurrentStep(3);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // convert to yyyy-mm-dd
      const formattedDate = selectedDate.toLocaleDateString('en-CA');
      
      // Format time as HH:MM (convert from "1:00pm" format)
      const timeMatch = selectedTime.match(/(\d+):(\d+)([ap]m)/i);
      let hours = parseInt(timeMatch[1]);
      const minutes = timeMatch[2];
      const isPM = timeMatch[3].toLowerCase() === 'pm';
      
      // Convert to 24-hour format
      if (isPM && hours < 12) hours += 12;
      if (!isPM && hours === 12) hours = 0;
      
      const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
      
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: formattedDate,
        time: formattedTime,
        timezone: timezone,
        additionalInfo: formData.additionalInfo
      };
      
      const response = await bookDemoCall(bookingData);
      
      if (response.success) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
        setSubmitError(response.message || 'Failed to book the call. Please try again.');
      }
    } catch (error) {
      console.error('Error booking meeting:', error);
      setSubmitStatus('error');
      setSubmitError('Failed to book the call. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  // Calendar generation helpers
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  
  const isDateAvailable = (day) => {
    return availableDates.some(date => 
      date.getDate() === day && 
      date.getMonth() === currentMonth && 
      date.getFullYear() === currentYear
    );
  };
  
  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentMonth === today.getMonth() && 
           currentYear === today.getFullYear();
  };
  
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];
    
    // Adjust for Monday as first day of week
    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const available = isDateAvailable(day);
      days.push(
        <div key={day} className="text-center">
          {available ? (
            <button
              onClick={() => handleDateClick(new Date(currentYear, currentMonth, day))}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm
                ${isToday(day) ? 'bg-blue-600 text-white' : 'hover:bg-blue-100 text-blue-600'}`}
            >
              {day}
            </button>
          ) : (
            <span className="w-8 h-8 flex items-center justify-center text-sm text-gray-400">
              {day}
            </span>
          )}
        </div>
      );
    }
    
    return days;
  };
  
  const formatDate = (date) => {
    if (!date) return '';
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  // Get timezone options
  const getTimezoneOptions = () => {
    // Common timezones
    const commonTimezones = [
      { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
      { value: 'America/New_York', label: 'Eastern Time (ET)' },
      { value: 'America/Chicago', label: 'Central Time (CT)' },
      { value: 'America/Denver', label: 'Mountain Time (MT)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
      { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
      { value: 'Europe/Paris', label: 'Central European Time (CET)' },
      { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
      { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' }
    ];
    
    // Find the user's timezone in the list or add it
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!commonTimezones.find(tz => tz.value === userTimezone)) {
      commonTimezones.unshift({
        value: userTimezone,
        label: `${userTimezone.replace('_', ' ').split('/')[1] || userTimezone} (Your local time)`
      });
    }
    
    return commonTimezones;
  };

  return (
    <section className="py-20 bg-background" id="book-call">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            Book a{' '}
            <span className="bg-gradient-to-r from-accent to-warning bg-clip-text text-transparent">
              Call
            </span>
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-3xl mx-auto">
            Let's discuss your project and how I can help bring your vision to life. Choose a time that works for you.
          </p>
        </motion.div>

        {/* Booking Widget */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Left Panel - Info */}
              <div className="bg-gray-50 p-8 flex flex-col">
                <div className="mb-8">
                  <img 
                    src="avatar.jpg" 
                    alt="Nikunj Navdiya" 
                    className="h-16 w-16 mb-6 rounded-full shadow-gray-500 shadow-lg object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                  <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                    Let's chat
                  </h3>
                  <p className="font-body text-gray-600 mb-4">
                    15 min
                  </p>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Icon name="Video" size={18} />
                    <p className="font-body text-sm">
                      Web conferencing details provided upon confirmation.
                    </p>
                  </div>
                </div>
                
                {currentStep >= 2 && selectedDate && (
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Calendar" size={18} className="text-gray-600" />
                      <p className="font-body text-gray-800 font-medium">
                        {formatDate(selectedDate)}
                      </p>
                    </div>
                    {currentStep >= 3 && selectedTime && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Clock" size={18} className="text-gray-600" />
                        <p className="font-body text-gray-800 font-medium">
                          {selectedTime}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="mt-auto">
                  <p className="font-body text-gray-600 text-sm">
                    Whether you want to see a demo, ask questions on a specific product, or get ideas on how to use my services in your community, I'm here to help.
                  </p>
                </div>
              </div>
              
              {/* Right Panel - Booking Steps */}
              <div className="p-8">
                {/* Step 1: Date Selection */}
                {currentStep === 1 && (
                  <div>
                    <h3 className="font-heading text-xl font-bold text-gray-900 mb-6">
                      Select a Date & Time
                    </h3>
                    
                    {/* Calendar Navigation */}
                    <div className="flex items-center justify-between mb-4">
                      <button onClick={goToPrevMonth} className="text-gray-600 hover:text-gray-900">
                        <Icon name="ChevronLeft" size={20} />
                      </button>
                      <h4 className="font-body font-medium text-gray-900">
                        {monthNames[currentMonth]} {currentYear}
                      </h4>
                      <button onClick={goToNextMonth} className="text-gray-600 hover:text-gray-900">
                        <Icon name="ChevronRight" size={20} />
                      </button>
                    </div>
                    
                    {/* Calendar */}
                    <div className="mb-6">
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map(day => (
                          <div key={day} className="text-center font-body text-xs text-gray-500">
                            {day}
                          </div>
                        ))}
                      </div>
                      {isLoading ? (
                        <div className="flex justify-center items-center h-48">
                          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-7 gap-1">
                          {renderCalendar()}
                        </div>
                      )}
                    </div>
                    
                    {/* Timezone */}
                    <div className="mt-6">
                      <p className="font-body text-sm text-gray-600 mb-2">
                        Time zone
                      </p>
                      <div className="flex items-center space-x-2">
                        <Icon name="Globe" size={16} className="text-gray-600" />
                        <select 
                          className="font-body text-sm text-gray-800 bg-transparent focus:outline-none"
                          value={timezone}
                          onChange={(e) => setTimezone(e.target.value)}
                        >
                          {getTimezoneOptions().map(tz => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 2: Time Selection */}
                {currentStep === 2 && (
                  <div>
                    <div className="flex items-center mb-6">
                      <button onClick={handlePrevStep} className="mr-4">
                        <Icon name="ChevronLeft" size={20} className="text-gray-600" />
                      </button>
                      <h3 className="font-heading text-xl font-bold text-gray-900">
                        {formatDate(selectedDate)}
                      </h3>
                    </div>
                    
                    {isLoading ? (
                      <div className="flex justify-center items-center h-48">
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 max-h-[300px] overflow-y-auto gap-2">
                        {availableTimes.length > 0 ? (
                          availableTimes.map(time => (
                            <button
                              key={time}
                              onClick={() => handleTimeClick(time)}
                              className="w-full py-4 px-6 text-center border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors duration-200 font-body text-blue-600"
                            >
                              {time}
                            </button>
                          ))
                        ) : (
                          <p className="text-center text-gray-500 py-8">
                            No available time slots for this date. Please select another date.
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Timezone */}
                    <div className="mt-6">
                      <div className="flex items-center space-x-2">
                        <Icon name="Globe" size={16} className="text-gray-600" />
                        <select 
                          className="font-body text-sm text-gray-800 bg-transparent focus:outline-none"
                          value={timezone}
                          onChange={(e) => setTimezone(e.target.value)}
                        >
                          {getTimezoneOptions().map(tz => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 3: Details Form */}
                {currentStep === 3 && (
                  <div>
                    <div className="flex items-center mb-6">
                      <button onClick={handlePrevStep} className="mr-4">
                        <Icon name="ChevronLeft" size={20} className="text-gray-600" />
                      </button>
                      <h3 className="font-heading text-xl font-bold text-gray-900">
                        Enter Details
                      </h3>
                    </div>
                    
                    {submitStatus === 'success' ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="Check" size={32} className="text-green-600" />
                        </div>
                        <h4 className="font-heading text-xl font-bold text-gray-900 mb-2">
                          Booking Confirmed!
                        </h4>
                        <p className="font-body text-gray-600 mb-6">
                          You'll receive a confirmation email with all the details and Google Meet link.
                        </p>
                        <button 
                          onClick={() => {
                            setCurrentStep(1);
                            setSelectedDate(null);
                            setSelectedTime(null);
                            setSubmitStatus(null);
                          }}
                          className="font-body text-blue-600 hover:text-blue-800"
                        >
                          Book another call
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        {submitStatus === 'error' && (
                          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                            <p className="font-body text-sm">{submitError}</p>
                          </div>
                        )}
                        
                        <div>
                          <label className="block font-body font-medium text-gray-700 mb-1">
                            Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block font-body font-medium text-gray-700 mb-1">
                            Email *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block font-body font-medium text-gray-700 mb-1">
                            Phone *
                          </label>
                          <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block font-body font-medium text-gray-700 mb-1">
                            Share anything that would be helpful for our call.
                          </label>
                          <textarea
                            name="additionalInfo"
                            value={formData.additionalInfo}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        
                        <div className="pt-4">
                          <p className="font-body text-xs text-gray-600 mb-4">
                            By proceeding, you confirm that you have read and agree to my Terms of Use and Privacy Notice.
                          </p>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-body font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                          >
                            {isSubmitting ? (
                              <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                <span>Scheduling...</span>
                              </>
                            ) : (
                              <span>Schedule Event</span>
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;